import React, { useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { BsFillTrashFill } from 'react-icons/bs';
import { useParams } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import { capacityApi } from '@reducers/capacity';

const FormErrors = () => {
  const { errors } = useFormikContext();
  return errors.primaryAddresses ? (
    <div className="invalid-feedback mb-2" style={{ display: 'block' }}>
      {errors.primaryAddresses}
    </div>
  ) : null;
};

function generateID() {
  return Math.random().toString(16).slice(2);
}

export const TrunkAddressesForm = ({ data }) => {
  const { trunkID } = useParams();

  const [addressesData, setAddressesData] = useState(data);

  const [deleteQuery] = capacityApi.useDeleteUserTrunkAddressMutation();
  const [createQuery] = capacityApi.useCreateUserTrunkAddressMutation();
  const [updateQuery] = capacityApi.useUpdateUserTrunkAddressMutation();

  const addEmptyRow = () => {
    const id = generateID();
    setAddressesData([
      ...addressesData,
      {
        id: `new-${id}`,
        address: '',
        port: 5060,
        failover: false,
        primary: false,
      },
    ]);
  };

  const removeEmptyRow = (id) => {
    setAddressesData([...addressesData].filter((item) => item.id !== id));
  };

  const isNew = (id) => String(id).startsWith('new');

  const onSubmit = async (values, actions) => {
    const formData = Object.values(values);
    const createData = formData.filter(({ id }) => isNew(id));
    const updateData = formData.filter(({ id }) => !isNew(id));

    actions.setSubmitting(true);
    if (createData.length > 0) {
      createQuery({ data: createData, trunkID })
        .unwrap()
        .then(() => {})
        .catch(() => {
          actions.setFieldError('name', 'Server-side error occurred.');
          actions.setSubmitting(false);
        });
    }
    if (updateData.length > 0) {
      updateQuery({ data: updateData, trunkID })
        .unwrap()
        .then(() => {})
        .catch(() => {
          actions.setFieldError('name', 'Server-side error occurred.');
          actions.setSubmitting(false);
        });
    }
  };

  const initialValues = {};
  const schemaObjects = {};
  addressesData.forEach((item) => {
    initialValues[item.id] = item;
    schemaObjects[item.id] = Yup.object()
      .shape({
        address: Yup.string().label('Address').required(),
        port: Yup.string().label('Port').required(),
        failover: Yup.boolean().label('Failover'),
        primary: Yup.boolean().label('Primary'),
      })
      .test('testFailoverPrimary', null, (obj) => {
        if (obj.primary && obj.failover) {
          return new Yup.ValidationError(
            'Only one can be chosen - primary or failover',
            null,
            `${obj.id}.failoverPrimary`
          );
        }
        if (obj.primary || obj.failover) {
          return true;
        }
        return new Yup.ValidationError(
          'One of primary or failover is required.',
          null,
          `${obj.id}.failoverPrimary`
        );
      });
  });
  const validationSchema = Yup.object()
    .shape({ ...schemaObjects })
    .test('testPrimary', null, (obj) => {
      const testData = Object.values(obj);
      const primaryData = testData
        .map(({ primary }) => primary)
        .filter((item) => item === true);
      if (primaryData.length === 0) {
        return new Yup.ValidationError(
          'Primary trunk address is required.',
          null,
          `primaryAddresses`
        );
      }
      if (primaryData.length > 1) {
        return new Yup.ValidationError(
          'Only one primary trunk address is allowed.',
          null,
          `primaryAddresses`
        );
      }
      return true;
    });
  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
      }) => (
        <Form onSubmit={handleSubmit}>
          <Table borderless responsive>
            <thead>
              <tr>
                <th>Address</th>
                <th>Port</th>
                <th className="text-center">Failover</th>
                <th className="text-center">Primary</th>
                <th className="text-center">Delete</th>
              </tr>
            </thead>
            <tbody>
              {addressesData?.map((item) => {
                const { id: accessor } = item;
                return (
                  <tr key={accessor}>
                    <Form.Group
                      as="td"
                      controlId={`${accessor}-formGroupAddress`}
                    >
                      <Form.Control
                        name={`${accessor}.address`}
                        type="input"
                        size="sm"
                        value={values[accessor]?.address || ''}
                        isValid={
                          touched[accessor]?.address &&
                          !errors[accessor]?.address
                        }
                        isInvalid={
                          touched[accessor]?.address &&
                          errors[accessor]?.address
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Address"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors[accessor]?.address}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as="td" controlId="formGroupPort">
                      <Form.Control
                        name={`${accessor}.port`}
                        type="input"
                        size="sm"
                        value={values[accessor]?.port || ''}
                        isValid={
                          touched[accessor]?.port && !errors[accessor]?.port
                        }
                        isInvalid={
                          touched[accessor]?.port && errors[accessor]?.port
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Port"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors[accessor]?.port}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as="td" controlId="formGroupFailover">
                      <Form.Check
                        className="text-center"
                        name={`${accessor}.failover`}
                        type="checkbox"
                        size="sm"
                        checked={values[accessor]?.failover || false}
                        value={values[accessor]?.failover || false}
                        isValid={
                          touched[accessor]?.failover &&
                          !errors[accessor]?.failoverPrimary &&
                          !errors.primaryAddresses
                        }
                        isInvalid={
                          (touched[accessor]?.failover &&
                            errors[accessor]?.failoverPrimary) ||
                          errors.primaryAddresses
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        feedback={errors[accessor]?.failoverPrimary}
                        feedbackType="invalid"
                      />
                    </Form.Group>
                    <Form.Group as="td" controlId="formGroupPrimary">
                      <Form.Check
                        className="text-center"
                        name={`${accessor}.primary`}
                        type="checkbox"
                        size="sm"
                        checked={values[accessor]?.primary || false}
                        value={values[accessor]?.primary || false}
                        isValid={
                          touched[accessor]?.primary &&
                          !errors[accessor]?.failoverPrimary &&
                          !errors.primaryAddresses
                        }
                        isInvalid={
                          (touched[accessor]?.primary &&
                            errors[accessor]?.failoverPrimary) ||
                          errors.primaryAddresses
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        feedback={errors[accessor]?.failoverPrimary}
                        feedbackType="invalid"
                      />
                    </Form.Group>
                    <td className="text-center">
                      <BsFillTrashFill
                        size={17}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          !isNew(accessor)
                            ? deleteQuery({ id: accessor, trunkID })
                            : removeEmptyRow(accessor)
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <FormErrors />
          <ButtonSplitGroup>
            <Button
              size="sm"
              type="submit"
              variant="didxl"
              onClick={handleSubmit}
            >
              Save
            </Button>
            <Button size="sm" variant="didxl" onClick={addEmptyRow}>
              +
            </Button>
          </ButtonSplitGroup>
        </Form>
      )}
    </Formik>
  );
};
